/**
 * TierBadge Component
 * Displays verification tier badge with icon and tooltip
 */
import React from 'react';

const TIER_CONFIG = {
  T0: {
    label: 'Unverified',
    icon: null,
    color: 'bg-gray-100 text-gray-600',
    tooltip: 'Not yet verified',
    visible: false // Don't show T0 badge on cards
  },
  T1: {
    label: 'Verified',
    icon: '✓',
    color: 'bg-blue-100 text-blue-700',
    tooltip: 'Identity verified & payout-ready',
    visible: true
  },
  T2: {
    label: 'Pro',
    icon: '⭐',
    color: 'bg-yellow-100 text-yellow-700',
    tooltip: 'High ratings, on-time delivery, reviewed portfolio',
    visible: true
  },
  T3: {
    label: 'Elite Partner',
    icon: '🛡️',
    color: 'bg-purple-100 text-purple-700',
    tooltip: 'Sustained excellence; vetted by Rnkem',
    visible: true
  }
};

const TierBadge = ({ tier, showUnverified = false, size = 'sm' }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.T0;
  
  // Don't render T0 badge unless explicitly requested
  if (!config.visible && !showUnverified) {
    return null;
  }
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-1.5',
    lg: 'text-lg px-4 py-2'
  };
  
  return (
    <div className="inline-flex items-center group relative">
      <span
        className={`
          inline-flex items-center gap-1 rounded-full font-medium
          ${config.color}
          ${sizeClasses[size]}
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
          transition-all duration-200
        `}
        role="status"
        aria-label={`${config.label} tier - ${config.tooltip}`}
        tabIndex="0"
      >
        {config.icon && <span className="leading-none">{config.icon}</span>}
        <span>{config.label}</span>
      </span>
      
      {/* Tooltip */}
      <div
        className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          px-3 py-2 bg-gray-900 text-white text-xs rounded-lg
          opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
          pointer-events-none transition-opacity duration-200
          whitespace-nowrap z-10
          shadow-lg
        "
        role="tooltip"
      >
        {config.tooltip}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
          <div className="border-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
};

export default TierBadge;
